import { useCallback, useState } from 'react'
import Image from '~/components/image/Image'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import type { DisplaySeat } from '~/features/reservationInput/store/stateMachine/statemachine'
import { useIsPC } from '~/lib/ua'
import { useBack, useReserveOverlay } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { Button } from '~/ui'
import { Description } from './Description'

export function SeatDetailModal() {
  const back = useBack()
  const close = useCallback(() => {
    back()
  }, [back])
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_SEAT_MODAL')
  if (context === undefined) {
    return null
  }
  return (
    <StackableSlideUpModal willBeUnmounted={willBeUnmounted}>
      <Body seat={context.seat} close={close} />
    </StackableSlideUpModal>
  )
}

const styles = sva({
  slots: ['container', 'label', 'image', 'note'],
  base: {
    container: {
      padding: '4',
      spaceY: '3',
      _pc: {
        padding: '6',
        spaceY: '5',
      },
    },
    label: {
      fontSize: 'md',
      fontWeight: 'bold',
      _pc: {
        fontSize: 'xl',
      },
    },
    image: {
      objectFit: 'cover',
      objectPosition: 'center',
      rounded: 'sm',
    },
    note: {
      textAlign: 'right',
      fontSize: 'xs',
      color: 'gray.600',
    },
  },
})

function Body({ seat, close }: { seat: DisplaySeat; close: () => void }) {
  const classes = styles()
  return (
    <div className={classes.container}>
      <div className={css({ spaceY: '2' })}>
        <SeatImage seat={seat} />
        <p className={classes.label}>{seat.label}</p>
        <Description seat={seat} />
        {seat.description !== '' && <p>{seat.description}</p>}
      </div>
      <Button color="tertiary" size="md" spread={true} onClick={close}>
        閉じる
      </Button>
    </div>
  )
}

function useSeatImage() {
  const [noImage, setNoImage] = useState(false)
  const onError = useCallback(() => {
    setNoImage(true)
  }, [])
  return { noImage, onError }
}

function SeatImage({ seat }: { seat: DisplaySeat }) {
  const isPC = useIsPC()
  const { noImage, onError } = useSeatImage()
  if (seat.image === null || noImage) {
    return null
  }
  const classes = styles()
  return (
    <div className={css({ spaceY: '1' })}>
      <Image
        alt={seat.label}
        src={seat.image.url}
        full={true}
        className={classes.image}
        onError={onError}
        width={isPC ? 620 : 343}
        height={isPC ? 294 : 160}
      />
      <div className={classes.note}>※写真はイメージです</div>
    </div>
  )
}
